export const productivityEducation = {
  // Main navigation
  title: 'Productivity Education',
  description: 'Master productivity techniques and learn to use the app effectively',
  dashboard: 'Dashboard',
  guidesNav: 'Guides',
  coursesNav: 'Courses',
  exercisesNav: 'Exercises',
  challengesNav: 'Challenges',
  leaderboard: 'Leaderboard',
  myProgress: 'My Progress',

  // Stats and metrics
  level: 'Level',
  points: 'points',
  coursesCompleted: 'Courses Completed',
  started: 'started',
  lessonsCompleted: 'Lessons Completed',
  keepLearning: 'Keep learning!',
  learningStreak: 'Learning Streak',
  days: 'days',
  minutes: 'minutes',
  min: 'min',
  lessons: 'lessons',

  // Quick access cards
  guidesDesc: 'Step-by-step tutorials for quick learning',
  coursesDesc: 'Structured courses on productivity techniques',
  exercisesDesc: 'Practice what you learn with hands-on exercises',
  challengesDesc: 'Test your skills with daily and weekly challenges',
  leaderboardDesc: 'Compare your progress with other learners',
  progressDesc: 'Track your learning journey and achievements',

  // Actions
  explore: 'Explore',
  startLearning: 'Start Learning',
  practice: 'Practice',
  viewChallenges: 'View Challenges',
  viewRankings: 'View Rankings',
  viewProgress: 'View Progress',
  startCourse: 'Start Course',
  start: 'Start',
  review: 'Review',
  backToCourses: 'Back to Courses',
  backToCourse: 'Back to Course',
  markComplete: 'Mark as Complete',

  // Categories
  categories: {
    all: 'All Courses',
    fundamentals: 'Fundamentals',
    'time-management': 'Time Management',
    goals: 'Goals',
    habits: 'Habits',
    tasks: 'Tasks',
    focus: 'Focus & Deep Work',
    tools: 'Advanced Tools',
  },

  // Difficulty levels
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  advanced: 'Advanced',

  // Course details
  productivityCourses: 'Productivity Courses',
  yourProgress: 'Your Progress',
  progress: 'Progress',
  completed: 'Completed',
  notStarted: 'Not started',
  courseLessons: 'Course Lessons',
  videoPlayer: 'Video Player',

  // Messages
  recentActivity: 'Recent Activity',
  recentActivityDesc: 'Your learning journey',
  noActivityYet: 'No activity yet. Start learning to track your progress!',
  courseNotFound: 'Course not found',
  lessonNotFound: 'Lesson not found',
  invalidLesson: 'Invalid lesson',
  noLessonsYet: 'No lessons available for this course yet',
  noCoursesInCategory: 'No courses available in this category',
  
  // Quick access and page headers
  quickGuides: 'Quick Guides',
  quickGuidesDesc: 'Step-by-step tutorials for quick learning',
  practicalExercises: 'Practical Exercises',
  practicalExercisesDesc: 'Practice what you learn with hands-on exercises',
  productivityChallenges: 'Productivity Challenges',
  challengesDetailDesc: 'Test your skills with challenges',
  globalLeaderboard: 'Global Leaderboard',
  leaderboardDetailDesc: 'Compare your progress with other learners',
  myLearningProgress: 'My Learning Progress',
  progressDetailDesc: 'Track your learning journey and achievements',
  
  // Additional keys
  steps: 'steps',
  courses: 'courses',
  startGuide: 'Start Guide',
  startExercise: 'Start Exercise',
  continue: 'Continue',
  joinChallenge: 'Join Challenge',
  allChallenges: 'All Challenges',
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  allTime: 'All Time',
  thisMonth: 'This Month',
  thisWeek: 'This Week',
  topLearners: 'Top Learners',
  topLearnersDesc: 'The learners with the most points of all time',
  thisMonthLeaders: 'This Month Leaders',
  thisMonthLeadersDesc: 'The best learners of this month',
  thisWeekLeaders: 'This Week Leaders',
  thisWeekLeadersDesc: 'The best learners of this week',
  anonymousUser: 'Anonymous User',
  currentLevel: 'Current Level',
  progressToNext: 'Progress to next',
  pointsUntilNext: 'points until next level',
  keepGoing: 'Keep Going!',
  streakMotivation: 'Keep learning every day to maintain your streak',
  courseProgress: 'Course Progress',
  courseProgressDesc: 'Your progress in courses',
  learningStats: 'Learning Statistics',
  totalTimeInvested: 'Total Time Invested',
  totalPoints: 'Total Points',
  availableExercises: 'Available Exercises',
  badgesAndAchievements: 'Badges and Achievements',
  
  // Guides
  guides: {
    pomodoroGuide: 'How to Use Pomodoro Timer',
    pomodoroGuideDesc: 'Learn to use the Pomodoro technique to improve your focus',
    goalSettingGuide: 'Setting Your First SMART Goal',
    goalSettingGuideDesc: 'Learn to create effective and achievable goals',
    habitTrackingGuide: 'Tracking Habits Effectively',
    habitTrackingGuideDesc: 'Master the habit tracking system',
    timeBlockingGuide: 'Time Blocking for Productivity',
    timeBlockingGuideDesc: 'Organize your day with effective time blocks',
    taskPrioritizationGuide: 'Task Prioritization with Matrices',
    taskPrioritizationGuideDesc: 'Learn to prioritize tasks using decision matrices',
    analyticsGuide: 'Using Analytics to Optimize',
    analyticsGuideDesc: 'Interpret your metrics to continuously improve',
  },
  
  // Exercises
  exercises: {
    timeAudit: 'Personal Time Audit',
    timeAuditDesc: 'Track how you use your time for 3 days',
    pomodoroChallenge: '4-Session Pomodoro Challenge',
    pomodoroChallengeDesc: 'Complete 4 Pomodoro sessions in one day',
    goalBreakdown: 'Goal Breakdown into Milestones',
    goalBreakdownDesc: 'Break down a big goal into actionable steps',
    habitStacking: 'Morning Habit Stack',
    habitStackingDesc: 'Build a routine of 3 consecutive habits',
    deepWorkSession: 'Deep Work Session',
    deepWorkSessionDesc: 'Complete 2 hours of deep work without distractions',
    weeklyReview: 'Complete Weekly Review',
    weeklyReviewDesc: 'Perform a thorough review of your week',
  },
  
  // Challenges
  challenges: {
    dailyPomodoro: 'Daily Pomodoro Master',
    dailyPomodoroDesc: 'Complete 4 Pomodoro sessions today',
    morningRoutine: 'Perfect Morning Routine',
    morningRoutineDesc: 'Complete your morning routine before 9 AM',
    weeklyGoals: 'Weekly Goal Review',
    weeklyGoalsDesc: 'Review and update your goals every day this week',
    taskMaster: 'Task Master',
    taskMasterDesc: 'Complete 50 tasks this week',
    deepWorkMonth: 'Deep Work Month',
    deepWorkMonthDesc: 'Accumulate 20 hours of deep work this month',
    productivityMaster: 'Productivity Master',
    productivityMasterDesc: 'Complete 30 days of consistent activity',
  },
};
